/**
 * §7.2 覆核者的落點：`/verify` **待覆核**。
 *
 * 誠實說明目前的限制：mockup 的模型把 `verifiedBy` 記在量測事件本身
 * （輸入當下就填），沒有「事後覆核」的事件型別。所以這一頁目前是**唯讀清單**——
 * 要能事後補覆核，需要新增一種領域事件，那是規格層面的決定，不是實作細節。
 */

import { useQuery } from '../api/hooks.js'
import { Alert, Card, Empty, Mono, Pill, Table, Td } from '../components/ui.js'
import { useDict } from '../config/ConfigContext.js'
import { kgUnit, tsShort } from '../../../shared/lib/format.js'
import { Link } from '../router.js'

interface Pending {
  siteId: string
  items: {
    event_id: string
    measurement_point_id: string
    scale_id: string
    panel_id: string | null
    receipt_line_id: string | null
    issue_line_id: string | null
    work_order_id: string | null
    net_kg: string
    entry_method: string
    entered_by: string
    entered_at: string
    validation_flags: string[]
  }[]
  nextCursor: string | null
  dualCheckPoints?: string[]
  note?: string
}

export function VerifyPending() {
  const dict = useDict()
  const { data, error, loading } = useQuery<Pending>('/api/verify/pending?limit=100')

  return (
    <div className="space-y-4">
      <Card
        title="待覆核的量測"
        subtitle={data?.dualCheckPoints?.length
          ? `要求雙人覆核的量測點：${data.dualCheckPoints.join('、')}`
          : undefined}
      >
        {loading && <p className="text-sm text-ink-500">載入中…</p>}
        {error && <Alert tone="danger" title="讀不到清單">{error.message}</Alert>}
        {data?.note && <Alert tone="info" title="目前沒有需要覆核的量測點">{data.note}</Alert>}

        {data && data.items.length === 0 && !data.note && (
          <Empty>沒有待覆核的量測。</Empty>
        )}

        {data && data.items.length > 0 && (
          <Table head={['量測點', '對象', '淨重', '方式', '輸入人', '時間', '旗標']}>
            {data.items.map((w) => (
              <tr key={w.event_id}>
                <Td className="text-xs"><Mono>{w.measurement_point_id}</Mono></Td>
                <Td className="text-xs">
                  {w.panel_id
                    ? <Link to={`/panel/${encodeURIComponent(w.panel_id)}`} className="text-sky-700">
                        <Mono>{w.panel_id}</Mono>
                      </Link>
                    : w.work_order_id
                      ? <Link to={`/balance/${w.work_order_id}`} className="text-sky-700">
                          <Mono>{w.work_order_id}</Mono>
                        </Link>
                      : <span className="text-ink-400">整批</span>}
                </Td>
                <Td className="tabular-nums">{kgUnit(Number(w.net_kg), 2)}</Td>
                <Td className="text-xs">{w.entry_method === 'instrument' ? '設備讀值' : '手動輸入'}</Td>
                <Td className="text-xs">{w.entered_by}</Td>
                <Td className="text-xs">{tsShort(w.entered_at)}</Td>
                <Td>{w.validation_flags.map((f) => <Pill key={f} tone="amber">{f}</Pill>)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Alert tone="info" title="事後補覆核還沒有對應的事件型別">
        目前的領域模型把覆核人記在量測事件本身（輸入當下由第二人確認並填入）。
        要能在這一頁事後補覆核，需要新增一種領域事件——那會改變事件流的定義，
        屬於規格層面的決定。詳見 <Mono>IMPLEMENTATION_STATUS.md</Mono> 的待決事項。
      </Alert>
    </div>
  )
}
